/**
 * Die Kampagnen-Parameter einer Landing-URL.
 *
 * Die Mail-Links der reply.io-Sequenzen tragen drei Werte:
 *
 *     /umfrage-unternehmen/ausschreibungen
 *         ?user_id={{HubSpotContactId}}     ← wer (bestehender Mechanismus)
 *         &utm_campaign=rfp-2026-09          ← welche Kampagne
 *         &utm_content=mail-1                ← welche Mail der Sequenz
 *
 * `user_id` liest `HubSpotIdentifyFromUrl` bereits. Diese Datei liest die zwei
 * uebrigen — fuer die Ereignisse im Browser. Der Server liest dieselben Werte
 * noch einmal aus dem Referer-Header (`packages/server/src/utils/url.ts`), weil
 * er den Browser-Zustand nicht kennt; die FELDNAMEN sind dort dieselben, damit
 * beide Seiten dasselbe meinen.
 */

/**
 * Die Mails, die eine Kampagne verschicken darf.
 *
 * Ein geschlossener Satz, keine Durchreiche. Der Wert kommt aus einer URL, die
 * jeder tippen kann, und landet in GA4-Ereignissen, aus denen jemand spaeter
 * eine Auswertung baut: «mail-1», «mail_1» und «Mail 1» waeren dort drei
 * Kampagnen, und das faellt erst Monate spaeter auf.
 *
 * Dieselbe Begruendung traegt `isJourney` (`tag-manager.ts:152`): Velite leitet
 * aus MDX nur `string` ab, und ein Tippfehler wuerde als eigene Reise im
 * Reporting auftauchen. Lieber kein Wert als ein falscher.
 */
export const CAMPAIGN_CONTENTS = ["mail-1", "mail-2"] as const;

export type CampaignContent = (typeof CAMPAIGN_CONTENTS)[number];

export const isCampaignContent = (value: string | undefined): value is CampaignContent =>
    value !== undefined && (CAMPAIGN_CONTENTS as readonly string[]).includes(value);

/**
 * Ein Kampagnenname, der in einem Ereignis stehen darf.
 *
 * Anders als die Mail ist er nicht aufzaehlbar — jede kuenftige Kampagne bringt
 * einen neuen —, also pruefen wir die FORM statt einer Liste: Kleinbuchstaben,
 * Ziffern, Bindestriche, hoechstens 40 Zeichen. Dieselbe Regel steht
 * serverseitig in `services/hubspot.ts`; sie begrenzt dort, was in eine
 * CRM-Notiz geschrieben wird.
 */
const CAMPAIGN_NAME = /^[a-z0-9][a-z0-9-]{0,39}$/;

export type CampaignParams = {
    campaign?: string;
    campaignContent?: CampaignContent;
};

/**
 * Liest und PRUEFT die Kampagnen-Parameter aus einer Suchzeichenkette.
 *
 * Ein unbekannter Wert wird weggelassen, nicht durchgereicht: Ein Ereignis mit
 * einer erfundenen Kampagne ist schlechter als eines ohne, weil es in jeder
 * Auswertung als eigene Zeile erscheint und niemand weiss, woher sie kommt.
 *
 * @param search - `window.location.search`, also `"?utm_campaign=…"` oder `""`.
 */
export const readCampaignParams = (search: string): CampaignParams => {
    if (!search) {
        return {};
    }

    const params = new URLSearchParams(search);
    const campaign = params.get("utm_campaign")?.trim();
    const content = params.get("utm_content")?.trim();

    return {
        ...(campaign && CAMPAIGN_NAME.test(campaign) ? { campaign } : {}),
        ...(isCampaignContent(content) ? { campaignContent: content } : {}),
    };
};

/**
 * Die Parameter, die eine interne Navigation ueberleben muessen.
 *
 * **Der Fall, gegen den das steht.** `trackLeadConversion` nimmt
 * `pathname + search` der AKTUELLEN Seite (`tag-manager.ts:132`). Wer von der
 * Kampagnenseite auf `/angebote/ki-potenzialanalyse` klickt und dort das
 * Formular abschickt, erzeugt eine Conversion ohne Kampagnenbezug — die Kette
 * Mail → Seite → Abschluss reisst genau an der Stelle, an der sie zaehlt.
 *
 * `user_id` gehoert dazu, weil `HubSpotIdentifyFromUrl` auf jeder Seite haengt
 * und den Besuch sonst nur einmal meldet.
 */
export const DURCHREICHEN = ["user_id", "utm_campaign", "utm_content"] as const;

/**
 * Haengt die weiterzureichenden Parameter an eine interne Adresse.
 *
 * Bestehende Parameter des Ziels gewinnen: Wer `?variant=b` verlinkt, meint es
 * so. Externe Adressen und Anker bleiben unberuehrt — ein `utm_campaign` an
 * einem `#kontakt` waere sinnlos, an einer fremden Domain waere es eine
 * Weitergabe, die niemand erwartet.
 */
export const mitKampagne = (href: string, search: string): string => {
    if (!search || !href.startsWith("/")) {
        return href;
    }

    const [pfad, eigene] = href.split("?");
    const ziel = new URLSearchParams(eigene ?? "");
    const quelle = new URLSearchParams(search);

    DURCHREICHEN.forEach(schluessel => {
        const wert = quelle.get(schluessel);
        if (wert && !ziel.has(schluessel)) {
            ziel.set(schluessel, wert);
        }
    });

    const zeichenkette = ziel.toString();
    return zeichenkette ? `${pfad}?${zeichenkette}` : pfad ?? href;
};

/**
 * Dasselbe fuer Links, die als Markdown im Content stehen.
 *
 * `RichText` wandelt `[Text](/pfad)` in ein `<a>` um (`ui/RichText.tsx:47`) —
 * die Adresse liegt also im Fliesstext, nicht in einem Feld. Ohne diese
 * Behandlung verlieren genau die Links die Kampagne, die den Besucher ins
 * Angebot fuehren.
 *
 * Nur interne Ziele werden angefasst; das Muster verlangt ein fuehrendes `/`,
 * also bleiben Anker und fremde Domains schon durch die Suche unberuehrt.
 */
export const markdownMitKampagne = (text: string, search: string): string => {
    if (!search) {
        return text;
    }
    return text.replace(/\[([^\]]+)\]\((\/[^)\s]*)\)/g, (_treffer, beschriftung: string, ziel: string) => {
        return `[${beschriftung}](${mitKampagne(ziel, search)})`;
    });
};
